// "Com nota" (with_nfse) e "Sem nota" (without_nfse) são filtros de presença:
// mutuamente exclusivos entre si e com os status específicos da nota.
export const NFSE_PRESENCE_VALUES = ['with_nfse', 'without_nfse']

/**
 * Resolve a nova seleção do filtro de status de nota fiscal aplicando a
 * exclusividade das opções de presença.
 *
 * - Se o usuário acabou de marcar "Com nota"/"Sem nota", a seleção vira apenas
 *   essa opção (exclusiva).
 * - Caso contrário (marcou um status específico), remove qualquer opção de
 *   presença que estivesse marcada.
 *
 * @param next     valores selecionados agora (vindo do Select multiple)
 * @param previous valores que estavam selecionados antes
 */
export const resolveNfseStatusSelection = (
  next: string[],
  previous: string[]
): string[] => {
  const added = next.filter((value) => !previous.includes(value))
  const addedPresence = added.find((value) =>
    NFSE_PRESENCE_VALUES.includes(value)
  )

  if (addedPresence) {
    return [addedPresence]
  }

  return next.filter((value) => !NFSE_PRESENCE_VALUES.includes(value))
}

export const REFUNDED_STATUS = 'refunded'

// Junta o param `refunded=true` ao `status` (como opção 'refunded') para o
// FilterTable exibir/editar tudo no mesmo multi-select.
export const mergeRefundedIntoStatus = <T extends Record<string, unknown>>(
  filters: T
): T => {
  const { refunded, ...rest } = filters as Record<string, unknown>
  if (refunded !== 'true') return filters
  const status = typeof rest.status === 'string' ? rest.status : ''
  return {
    ...rest,
    status: [status, REFUNDED_STATUS].filter(Boolean).join('|')
  } as unknown as T
}

// Inverso: tira 'refunded' do `status` e emite `refunded=true` separado.
export const splitRefundedFromStatus = (
  query: Record<string, unknown>
): Record<string, unknown> => {
  const { refunded: _refunded, ...rest } = query // eslint-disable-line @typescript-eslint/no-unused-vars
  const status = typeof rest.status === 'string' ? rest.status : ''
  if (!status.split('|').includes(REFUNDED_STATUS)) return rest
  const statusWithoutRefunded = status
    .split('|')
    .filter((s) => s && s !== REFUNDED_STATUS)
    .join('|')
  const { status: _status, ...restWithoutStatus } = rest // eslint-disable-line @typescript-eslint/no-unused-vars
  return {
    ...restWithoutStatus,
    ...(statusWithoutRefunded && { status: statusWithoutRefunded }),
    refunded: 'true'
  }
}

// "Atrasada" = aberta e vencida. Não é status da billing: a API traduz o apelido
// e soma com OU aos outros status marcados (SC-11297). Antes ia o valor composto
// `open_payment,due_date_lower_than=<hoje>`, cuja vírgula a API lia como outro
// filtro (E) na lista inteira e sumia com pendentes, expiradas e abertas a vencer.
export const OVERDUE_STATUS = 'overdue'

// Links e filtros salvos antigos ainda trazem o valor composto.
export const normalizeLegacyOverdue = (value: string): string =>
  value.includes('open_payment,due_date_lower_than') ? OVERDUE_STATUS : value
